import { API } from "@swivl-mono/api-helper";
import { warn } from "@swivl-mono/helpers";
import { GoogleLocation, PaginatedResponse, ReviewAggregatedFilterData, ReviewGetType, ReviewInterface, ReviewPlatform } from "@swivl-mono/types";
import { create } from "zustand";

import { Member } from "../Member/Member.Current";

interface LastLoadParams {
  status: ReviewGetType;
  page: number;
  limit: number;
  hasReply?: boolean;
  rating?: number;
  platform?: ReviewPlatform;
  locationId?: number[];
  deletedOnGoogle?: boolean;
}

interface StoreInterface {
  selectedReviews: { [id: number]: ReviewInterface };
  reviews?: ReviewInterface[];
  archivedReviews?: ReviewInterface[];
  paginatedReviews: PaginatedResponse<ReviewInterface>;
  aggregatedData: ReviewAggregatedFilterData;
  googleLocationsForApp: { [appId: number]: GoogleLocation[] };
}
const BlankState: StoreInterface = {
  selectedReviews: {},
  reviews: undefined,
  archivedReviews: undefined,
  paginatedReviews: { data: [], pagination: { page: 1, pageSize: 50, total: 0, totalPages: 0 } },
  aggregatedData: {
    status: { responded: 0, notResponded: 0, deletedOnGoogle: 0 },
    starRating: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
    provider: {},
    locations: []
  },
  googleLocationsForApp: {}
};

export default class ReviewsModel {
  static useState = create<StoreInterface>((set) => ({ ...BlankState }));
  static setState = ReviewsModel.useState.setState;
  private static lastLoadParams?: LastLoadParams;
  static get state() {
    return ReviewsModel.useState.getState();
  }
  static reset() {
    ReviewsModel.setState({ ...BlankState });
  }

  static async load(
    status: ReviewGetType,
    page: number = 1,
    limit: number = 50,
    hasReply?: boolean,
    rating?: number,
    platform?: ReviewPlatform,
    locationId?: number[],
    deletedOnGoogle?: boolean
  ): Promise<void> {
    ReviewsModel.lastLoadParams = { status, page, limit, hasReply, rating, platform, locationId, deletedOnGoogle };

    try {
      const paginatedResponse = await API.Reviews.getReviewsPaginated(
        Member.orgId,
        status,
        page,
        limit,
        hasReply,
        rating,
        platform,
        locationId,
        deletedOnGoogle
      );
      ReviewsModel.setState({
        paginatedReviews: paginatedResponse
      });
    } catch (e) {
      warn("Error Loading Reviews");
    }
  }

  static async loadAggregatedData(status: ReviewGetType): Promise<void> {
    try {
      const data = await API.Reviews.getAggregatedReviewData(Member.orgId, status);
      ReviewsModel.setState({
        aggregatedData: data
      });
    } catch (e) {
      console.error("Error loading aggregated data", e);
    }
  }

  static loadGoogleLocationsForApp(appId: number, force?: boolean) {
    if (!force && ReviewsModel.state.googleLocationsForApp[appId]) {
      return;
    }
    API.Google.getGoogleAccountAndLocationNames(Member.orgId, appId).then((places) => {
      let googleLocationsForApp = { ...ReviewsModel.state.googleLocationsForApp };
      googleLocationsForApp[appId] = places;
      ReviewsModel.setState({
        googleLocationsForApp: googleLocationsForApp
      });
    });
  }

  static async reloadLastQuery(): Promise<void> {
    if (!ReviewsModel.lastLoadParams) {
      return;
    }
    const { status, page, limit, hasReply, rating, platform, locationId, deletedOnGoogle } = ReviewsModel.lastLoadParams;
    await ReviewsModel.load(status, page, limit, hasReply, rating, platform, locationId, deletedOnGoogle);
    await ReviewsModel.loadAggregatedData(status);
  }

  static async replyToReview(review: ReviewInterface, replyText: string, shouldReload: boolean = true): Promise<ReviewInterface> {
    if (!review) {
      return Promise.reject("Missing review");
    }

    if (!review.id) {
      return Promise.reject("Missing review Id");
    }

    try {
      const updatedReview = await API.Reviews.replyToReview(Member.orgId, review.id, {
        replyText: replyText,
        repliedByName: Member.displayName,
        repliedByMemberId: Member.id!,
        replyPlatfrom: ReviewPlatform.SWIVL
      });

      let reviews = ReviewsModel.state.reviews ? [...ReviewsModel.state.reviews] : [];
      let index = reviews.findIndex((r) => {
        if (!r || !updatedReview) {
          return false;
        }
        return r.id === updatedReview.id;
      });
      if (index > -1) {
        reviews[index] = updatedReview;
      } else {
        reviews.push(updatedReview);
      }

      ReviewsModel.setState({
        reviews: reviews
      });

      if (shouldReload) {
        await ReviewsModel.reloadLastQuery();
      }

      return updatedReview;
    } catch (e) {
      warn("Error replying to review");
      return Promise.reject(e);
    }
  }

  static async deleteReviewReply(review: ReviewInterface): Promise<ReviewInterface> {
    if (!review) {
      return Promise.reject("Missing review");
    }

    try {
      const updatedReview = await API.Reviews.deleteReviewReply(Member.orgId, review.id);

      let reviews = ReviewsModel.state.reviews ? [...ReviewsModel.state.reviews] : [];
      let index = reviews.findIndex((r) => r.id === updatedReview.id);
      if (index > -1) {
        reviews[index] = updatedReview;
      } else {
        reviews.push(updatedReview);
      }

      ReviewsModel.setState({
        reviews: reviews
      });

      return updatedReview;
    } catch (e) {
      warn("Error deleting review reply");
      return Promise.reject(e);
    }
  }

  static async setArchivedOnReviews(reviews: ReviewInterface[], isArchived: boolean) {
    if (!reviews || reviews.length === 0) {
      return Promise.reject("Missing reviews");
    }
    API.Reviews.setArchivedOnReviews(Member.orgId, { ids: reviews.map((r) => r.id), isArchived })
      .then(() => {
        const unarchivedReviews = ReviewsModel.state.reviews ? [...ReviewsModel.state.reviews] : [];
        const archivedReviews = ReviewsModel.state.archivedReviews ? [...ReviewsModel.state.archivedReviews] : [];

        let sourceReviews = isArchived ? unarchivedReviews : archivedReviews;
        let targetReviews = isArchived ? archivedReviews : unarchivedReviews;

        reviews.forEach((review) => {
          review.isArchived = isArchived;
          let index = sourceReviews.findIndex((r) => r.id === review.id);
          if (index > -1) {
            sourceReviews.splice(index, 1);
          }
          index = targetReviews.findIndex((r) => r.id === review.id);
          if (index < 0) {
            targetReviews.push(review);
          }
        });
        if (isArchived) {
          ReviewsModel.setState({
            reviews: unarchivedReviews,
            archivedReviews: archivedReviews
          });
        } else {
          ReviewsModel.setState({
            reviews: unarchivedReviews,
            archivedReviews: archivedReviews
          });
        }

        ReviewsModel.deselectReviews(reviews);
      })
      .catch((e) => {
        warn("Error setArchivedOnReviews", e);
      });
  }

  static async setSelectedReviews(selectedReviews: { [id: number]: ReviewInterface }) {
    ReviewsModel.setState({ selectedReviews: selectedReviews });
  }

  static async setSelectOnReview(review: ReviewInterface, shouldSelect: boolean) {
    let selectedReviews = { ...ReviewsModel.state.selectedReviews };

    if (shouldSelect) {
      selectedReviews[review.id] = review;
    } else {
      delete selectedReviews[review.id];
    }
    ReviewsModel.setState({ selectedReviews: selectedReviews });
  }
  static async deselectAllReviews() {
    ReviewsModel.setState({ selectedReviews: {} });
  }
  static async deselectReviews(reviews?: ReviewInterface[]) {
    let selectedReviews = { ...ReviewsModel.state.selectedReviews };
    if (reviews) {
      reviews.forEach((review) => {
        delete selectedReviews[review.id];
      });
    }
    ReviewsModel.setState({ selectedReviews: selectedReviews });
  }

  static async testReply(review: ReviewInterface, replyText: string) {
    return new Promise<ReviewInterface>((resolve, reject) => {
      setTimeout(() => {
        if (Math.floor(Math.random() * 3) === 0) {
          return reject("Error");
        }

        review.replyText = replyText;
        let reviews = ReviewsModel.state.reviews ? [...ReviewsModel.state.reviews] : [];
        if (reviews) {
          let index = reviews.findIndex((r) => r.id === review.id);
          if (index > -1) {
            reviews[index] = review;
          } else {
            reviews.push(review);
          }
        }

        ReviewsModel.setState({
          reviews: reviews
        });

        resolve(review);
      }, 500);
    });
  }
}
